 
/* eslint-disable @typescript-eslint/no-explicit-any */
import { PlatformType } from "./platform-type";
import { PlatformUserAgent } from "./platform-user-agent";
import { type SystemInfoBuild, type SystemInfoWifiNetwork, type SystemInfoEthernetNetwork, type SystemInfoMemory, type TizenSystemInfoMemory, type TizenMemoryStatus } from "./platform-device-info";
import { PlatformDeviceInfo } from "./platform-device-info";

enum TizenStatus {
    ON = "ON",
    OFF = "OFF",
    CONNECTED = "CONNECTED",
    DISCONNECTED = "DISCONNECTED",
    DEACTIVATED = "DEACTIVATED",
}

export enum TizenOSVersion {
    Unknown = "unknown",
    V5_5 = "5.5",
    V6_0 = "6.0",
    V6_5 = "6.5",
    V7_0 = "7.0",
    V8_0 = "8.0",
    V9_0 = "9.0"
}

export class PlatformTizen extends PlatformUserAgent {

    constructor() {
        super(PlatformType.Tizen)
    }

    public static matchThisPlatform(): boolean {
        return window.navigator.userAgent.toLowerCase().includes("tizen");
    }

    isSupported(): boolean {
        return true;
    }

    getProperty = <T>(key: string): Promise<T> =>
        new Promise((resolve, reject) =>
            (window as any)?.tizen.systeminfo.getPropertyValue(key, resolve, reject)
        );

    async getDeviceInfo(): Promise<PlatformDeviceInfo> {
        const baseInfo = await super.getInfo();

        let model = "", manufacturer ="", buildVersion = "", wifiMacAddress = null, signalStrength = 0, cableMacAddress = "", ssid = null;
        let wifiStatus = "", cableStatus = "", cablePlugged = null;

        try {
            const build = await this.getProperty<SystemInfoBuild>("BUILD");
            model = build.model ?? "";
            manufacturer = build.manufacturer ?? "";
            buildVersion = build.buildVersion ?? "";
        } catch (error) {
            console.error(`API error get build: ${error}`);
        }
        try {
            const wifi = await this.getProperty<SystemInfoWifiNetwork>("WIFI_NETWORK");
            wifiMacAddress = wifi.macAddress;
            ssid = wifi.ssid;
            wifiStatus = wifi.status;
            signalStrength = wifi.signalStrength ?? 0;
        } catch (error) {
            console.error(`API error get wifi network: ${error}`);
        }
        try {
            const ethernet = await this.getProperty<SystemInfoEthernetNetwork>("ETHERNET_NETWORK");
            cableMacAddress = ethernet.macAddress ?? "";
            cableStatus = ethernet.status;
            cablePlugged = ethernet.cable;
        } catch (error) {
            console.error(`API error get ethernet network: ${error}`);
        }

        return new PlatformDeviceInfo(
            cableStatus !== TizenStatus.CONNECTED ? wifiMacAddress : cableMacAddress || baseInfo.macAddress,
            {
                model: model,
                manufacturer: manufacturer,
                buildVersion: buildVersion,
            },
            {
                macAddress: wifiMacAddress,
                signalStrength: signalStrength,
                ssid: ssid,
                status: wifiStatus,
            },
            {
                status: cableStatus,
                macAddress: cableMacAddress,
                cable: cablePlugged,
            }
        );
    }

    public getOSVersion(): string | null {
        // TODO: Implement get OS version for Tizen from the device API
        const ua = window.navigator.userAgent.toLowerCase();
        // eslint-disable-next-line no-useless-escape
        const match = ua.match(/tizen[ /]?([\d\.]+)/i);
        return match ? match[1] : null;
    }

    public async getMemoryInfo(): Promise<SystemInfoMemory> {
        let totalMemoryMB: string | null = null, availableMemoryMB: string | null = null, status: TizenMemoryStatus | null = null;

        const mb = (x: number): string => (((x / 1024 / 1024) * 10) / 10).toFixed(2) + ' MB';

        try {
            // Try to get memory status using getPropertyValue
            const memory = await this.getProperty<TizenSystemInfoMemory>("MEMORY");
            status = memory?.status ?? null;
        } catch (error) {
            console.error(`API error get memory status using getPropertyValue: ${error}`);
        }

        try {
            if ((window as any)?.tizen?.systeminfo?.getTotalMemory) {
                const totalMemoryBytes = (window as any).tizen.systeminfo.getTotalMemory();
                totalMemoryMB = totalMemoryBytes ? mb(totalMemoryBytes) : null;
            }
        } catch (error) {
            console.error(`API error get totalMemory: ${error}`);
        }

        try {
            if ((window as any)?.tizen?.systeminfo?.getAvailableMemory) {
                const availableMemoryBytes = (window as any).tizen.systeminfo.getAvailableMemory();
                availableMemoryMB = availableMemoryBytes ? mb(availableMemoryBytes) : null;
            }
        } catch (error) {
            console.error(`API error get availableMemory: ${error}`);
        }

        return {
            total: totalMemoryMB,
            available: availableMemoryMB,
            status: status,
        };
    }
};